import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
}

/**
 * PaginationBarComponent provides a reusable pagination UI: it displays current item ranges, page navigation controls (first/previous/next/last), and an items-per-page selector with an optional custom dropdown.
 * @component
 * @selector app-pagination-bar
 * @example
 *   <app-pagination-bar
 *     [paginationData]="pageData"
 *     [itemsPerPageOptions]="[10,25,50]"
 *     (pageChange)="onPageChange($event)"
 *     (itemsPerPageChange)="onItemsPerPageChange($event)">
 *   </app-pagination-bar>
 *
 * Description:
 *   - Presents a compact pagination bar that shows "Items X - Y of Z" based on provided paginationData.
 *   - Provides controls to navigate to first, previous, next, and last pages and emits pageChange events with the requested page number.
 *   - Allows selection of items-per-page via a native select or a custom dropdown; changes emit itemsPerPageChange with the new value.
 *   - Manages an internal dropdown open/close state and closes the dropdown when clicking outside via a HostListener on the document.
 *
 * Key Methods:
 *   - get paginationInfo(): string - Computes and returns a human-readable range of currently visible items or a "No items to display" message when totalItems is zero.
 *   - onFirstPage(): void - Emits pageChange(1) when not already on the first page.
 *   - onPreviousPage(): void - Emits pageChange(currentPage - 1) when a previous page exists.
 *   - onNextPage(): void - Emits pageChange(currentPage + 1) when a next page exists.
 *   - onLastPage(): void - Emits pageChange(totalPages) when not already on the last page.
 *   - onItemsPerPageChange(event: Event): void - Reads the selected value from a native select and emits itemsPerPageChange with the parsed number.
 *   - toggleDropdown(): void - Toggles the custom dropdown open state.
 *   - closeDropdown(): void - Forces the custom dropdown closed.
 *   - selectOption(option: number, event: Event): void - Stops event propagation, emits itemsPerPageChange(option), and closes the dropdown.
 *   - onDocumentClick(event: MouseEvent): void - HostListener that closes the custom dropdown when a click occurs outside the dropdown element.
 *   - trackByOption(index: number, option: number): number - trackBy function for ngFor on options, returning the option value for stable tracking.
 *
 * Lifecycle:
 *   - This component does not rely on Angular lifecycle hooks such as ngOnInit for its core behavior; it is driven by inputs and user interactions.
 *   - Uses HostListener('document:click') to handle outside clicks for dropdown management.
 *
 * @input {PaginationData} paginationData - The current pagination model (expected to include currentPage, itemsPerPage, totalItems, totalPages). Used to compute displayed range and enable/disable navigation.
 * @input {number[]} itemsPerPageOptions - Available numeric options for items per page (default: [10, 25, 50]).
 * @output {EventEmitter<number>} pageChange - Emitted when the user requests a different page; payload is the new page number.
 * @output {EventEmitter<number>} itemsPerPageChange - Emitted when the items-per-page selection changes; payload is the new items-per-page value.
 */
@Component({
  selector: 'app-pagination-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pagination-bar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pagination-bar.component.scss'],
})
export class PaginationBarComponent implements OnInit, OnDestroy {
  @Input() paginationData!: PaginationData;
  @Input() itemsPerPageOptions: number[] = [10, 25, 50];
  /**
   * `data-testid` stem. The desktop layout emits canonical hooks
   * (`<testId>-next-page-button`); the mobile layout, which is always in the DOM but hidden by a
   * media query, emits the same hooks with a `mobile-` infix so the two can never collide.
   */
  @Input() testId = 'pagination';

  @Output() pageChange = new EventEmitter<number>();
  @Output() itemsPerPageChange = new EventEmitter<number>();

  /** Desktop + mobile each render the dropdown; only the visible host gets a non-zero layout box. */
  @ViewChildren('dropdownTrigger', { read: ElementRef })
  private dropdownTriggers!: QueryList<ElementRef<HTMLElement>>;

  isDropdownOpen = false;

  /**
   * Fixed viewport positioning so the menu stays above the trigger and is not hidden by
   * table GPU layers (translateZ) or overflow on scroll parents.
   */
  dropdownMenuFixedStyle: Record<string, string> = {};

  private readonly repositionMenu = (): void => {
    if (this.isDropdownOpen) {
      this.updateFixedMenuPosition();
    }
  };

  ngOnInit(): void {
    document.addEventListener('scroll', this.repositionMenu, true);
    window.addEventListener('resize', this.repositionMenu);
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.repositionMenu, true);
    window.removeEventListener('resize', this.repositionMenu);
  }

  private getVisibleDropdownTrigger(): HTMLElement | null {
    if (!this.dropdownTriggers?.length) {
      return null;
    }
    for (const ref of this.dropdownTriggers) {
      const el = ref.nativeElement;
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return el;
      }
    }
    return null;
  }

  private updateFixedMenuPosition(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const trigger = this.getVisibleDropdownTrigger();
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    this.dropdownMenuFixedStyle = {
      position: 'fixed',
      left: `${Math.round(rect.left)}px`,
      width: `${Math.round(rect.width)}px`,
      bottom: `${Math.round(window.innerHeight - rect.top + gap)}px`,
      right: 'auto',
      top: 'auto',
      transform: 'none',
      'z-index': '10060',
    };
  }

  private clearFixedMenuStyle(): void {
    this.dropdownMenuFixedStyle = {};
  }

  get paginationInfo(): string {
    if (this.paginationData.totalItems === 0) {
      return 'No items to display';
    }

    const start = (this.paginationData.currentPage - 1) * this.paginationData.itemsPerPage + 1;
    const end = Math.min(
      this.paginationData.currentPage * this.paginationData.itemsPerPage,
      this.paginationData.totalItems
    );
    return `Items ${start} - ${end} of ${this.paginationData.totalItems}`;
  }

  onFirstPage(): void {
    if (this.paginationData.currentPage > 1) {
      this.pageChange.emit(1);
    }
  }

  onPreviousPage(): void {
    if (this.paginationData.currentPage > 1) {
      this.pageChange.emit(this.paginationData.currentPage - 1);
    }
  }

  onNextPage(): void {
    if (this.paginationData.currentPage < this.paginationData.totalPages) {
      this.pageChange.emit(this.paginationData.currentPage + 1);
    }
  }

  onLastPage(): void {
    if (this.paginationData.currentPage < this.paginationData.totalPages) {
      this.pageChange.emit(this.paginationData.totalPages);
    }
  }

  onItemsPerPageChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const newItemsPerPage = parseInt(select.value, 10);
    this.itemsPerPageChange.emit(newItemsPerPage);
  }

  toggleDropdown(): void {
    if (this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.clearFixedMenuStyle();
      return;
    }
    // Measure and set fixed coords before showing so the panel does not flash at (0,0) / absolute fallback.
    this.updateFixedMenuPosition();
    this.isDropdownOpen = true;
    requestAnimationFrame(() => this.updateFixedMenuPosition());
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
    this.clearFixedMenuStyle();
  }

  selectOption(option: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.itemsPerPageChange.emit(option);
    this.isDropdownOpen = false;
    this.clearFixedMenuStyle();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdown = target.closest('.custom-dropdown');

    if (!dropdown && this.isDropdownOpen) {
      this.isDropdownOpen = false;
      this.clearFixedMenuStyle();
    }
  }

  trackByOption(index: number, option: number): number {
    return option;
  }
}
